import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { isProduction } from '@epi-selectors/environment';
import { OverlaysConstants } from '@epi-constants/actions';

export type OverlayNotification = {
  uid: string | number;
  level?: string;
  message?: string;
  [key: string]: any;
};

export type ErrorPopupState = {
  display: boolean;
  closable: boolean;
  error?: any;
  showDetails?: boolean;
};

export type PasswordPopupState = {
  message: string;
  show: boolean;
  isLoading: boolean;
};

export type LoginPopupState = {
  show: boolean;
  isLoginPopup: boolean;
};

export type OverlaysState = {
  notifications: OverlayNotification[];
  errorPopup: ErrorPopupState;
  passwordPopup: PasswordPopupState;
  loginPopup: LoginPopupState;
  errorMessage?: string;
};

export const getInitialState = (): OverlaysState => ({
  notifications: [],
  errorPopup: {
    display: false,
    closable: true
  },
  passwordPopup: {
    message: '',
    show: false,
    isLoading: false
  },
  loginPopup: {
    show: false,
    isLoginPopup: true
  }
});

const initialState = getInitialState();

const overlaysSlice = createSlice({
  name: 'overlays',
  initialState,
  reducers: {
    setPasswordPopupVisibility: (
      state,
      {
        payload: { show, message = '' }
      }: PayloadAction<{ show: boolean; message?: string }>
    ) => {
      state.passwordPopup.show = show;
      state.passwordPopup.message = message;
    },
    setPasswordPopupLoading: (
      state,
      { payload: isLoading }: PayloadAction<boolean>
    ) => {
      state.passwordPopup.isLoading = isLoading;
    },
    setPasswordPopupError: (
      state,
      { payload: message }: PayloadAction<string>
    ) => {
      state.errorMessage = message;
    },
    clearPasswordPopupError: state => {
      state.errorMessage = '';
    }
  },
  extraReducers: builder => {
    builder
      .addMatcher(
        (action): action is OverlayNotification & { type: string } =>
          action.type === `${OverlaysConstants.SHOW_NOTIFICATION}`,
        (state, action) => {
          const { type: _type, ...options } = action;
          state.notifications.push(options);
        }
      )
      .addMatcher(
        (action): action is { type: string; uid: string | number } =>
          action.type === `${OverlaysConstants.HIDE_NOTIFICATION}`,
        (state, action) => {
          state.notifications = state.notifications.filter(
            notification => notification.uid !== action.uid
          );
        }
      )
      .addMatcher(
        action =>
          action.type === `${OverlaysConstants.REMOVE_ALL_NOTIFICATIONS}`,
        state => {
          state.notifications = [];
        }
      )
      .addMatcher(
        (action): action is OverlayNotification & { type: string } =>
          action.type === `${OverlaysConstants.SHOW_ERROR_NOTIFICATION}`,
        (state, action) => {
          const { type: _type, ...payload } = action;
          state.notifications.push({
            level: 'error',
            ...payload,
            uid: payload.uid ?? Date.now()
          });
        }
      )
      .addMatcher(
        (
          action
        ): action is {
          type: string;
          closable?: boolean;
          error?: any;
        } => action.type === `${OverlaysConstants.SHOW_ERROR_POPUP}`,
        (state, action) => {
          state.errorPopup = {
            display: true,
            closable: !(
              !state.errorPopup.closable ||
              (!action.closable && action.closable !== undefined)
            ),
            ...(action.error
              ? {
                  error: action.error,
                  showDetails: !isProduction()
                }
              : {})
          };
        }
      )
      .addMatcher(
        (action): action is { type: string; show: boolean; message?: string } =>
          action.type === `${OverlaysConstants.SET_PASSWORD_POPUP_VISIBILITY}`,
        (state, action) => {
          state.passwordPopup.show = action.show;
          state.passwordPopup.message = action.message || '';
        }
      )
      .addMatcher(
        (action): action is { type: string; isLoading: boolean } =>
          action.type ===
          `${OverlaysConstants.SET_PASSWORD_POPUP_LOADING_STATE}`,
        (state, action) => {
          state.passwordPopup.isLoading = action.isLoading;
        }
      )
      .addMatcher(
        (action): action is { type: string; message?: string } =>
          action.type ===
          `${OverlaysConstants.SET_ERROR_MESSAGE_ON_PASSWORD_POPUP}`,
        (state, action) => {
          state.errorMessage = action.message;
        }
      )
      .addMatcher(
        action =>
          action.type ===
          `${OverlaysConstants.SET_ERROR_MESSAGE_OFF_PASSWORD_POPUP}`,
        state => {
          state.errorMessage = '';
        }
      )
      .addMatcher(
        (action): action is { type: string; show: boolean } =>
          action.type === `${OverlaysConstants.SET_LOGIN_POPUP_VISIBILITY}`,
        (state, action) => {
          state.loginPopup.show = action.show;
          if (action.show) {
            state.loginPopup.isLoginPopup = true;
          }
        }
      )
      .addMatcher(
        (action): action is { type: string; isLoginPopup: boolean } =>
          action.type === `${OverlaysConstants.CHANGE_LOGIN_POPUP_CONTENT}`,
        (state, action) => {
          state.loginPopup.show = true;
          state.loginPopup.isLoginPopup = action.isLoginPopup;
        }
      );
  }
});

export const { reducer, actions: overlaysActions } = overlaysSlice;
